import { ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import type { Color, Position, LayersList } from "@deck.gl/core";
import type { LayerTypeSelectionMapLayerProps } from "../../types";
import { useMapLayer } from "react-migration/lib/map/useMapLayer";
import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { comparablesStore } from "src/js/stores/comparables";
import { userStore } from "src/js/stores/user/store";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import {
  textAccessors,
  characterSet,
} from "react-migration/domains/comparables/components/MapLayer/ComparablesLayer/comparablesLayerUtil";
import { CompositeLayer } from "@deck.gl/core";
import type { CorePriceModes } from "react-migration/domains/comparables/typings/PriceModes";
import { SelectionGeometry, SelectionType } from "src/js/stores/map/store";
import type { ComparableTile } from "react-migration/lib/typings/ComparablesTile";
import { Feature } from "@turf/helpers/lib/geojson";

interface ComparablesSelectionLayerProps {
  readonly selectionGeometry?: Feature<SelectionGeometry>;
  readonly fillColor: Color;
  readonly getText: (d: ComparableTile) => string;
}

class ComparablesSelectionLayer extends CompositeLayer<ComparablesSelectionLayerProps> {
  renderLayers(): LayersList {
    const { selectionGeometry, fillColor, getText } = this.props;

    const getPosition = (d: ComparableTile): Position => {
      const coords = d.geometry.coordinates as [number, number];
      return [coords[0], coords[1], 0];
    };

    const baseProps = {
      data: selectionGeometry ? [selectionGeometry] : [],
      pickable: false,
      getPosition,
    };

    const scatter = new ScatterplotLayer({
      id: "comparables-detail-selection-scatter",
      ...baseProps,
      getRadius: 12,
      radiusUnits: "pixels",
      radiusMinPixels: 1,
      radiusMaxPixels: 12,
      getFillColor: fillColor,
      getLineColor: [255, 255, 255, 255],
      getLineWidth: 3,
      lineWidthUnits: "pixels",
      stroked: true,
      filled: true,
      opacity: 0.9,
    });

    const text = new TextLayer({
      id: "comparables-detail-selection-text",
      ...baseProps,
      characterSet,
      getText,
      getAngle: 0,
      getPixelOffset: [0, -16],
      getSize: 14,
      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
      outlineWidth: 5,
      outlineColor: [255, 255, 255, 255],
      fontSettings: {
        sdf: true,
        fontSize: 32,
        buffer: 8,
      },
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      updateTriggers: {
        data: [selectionGeometry],
        getText: [getText],
      },
      opacity: 1,
    });

    return [scatter, text] as LayersList;
  }
}

export function ComparablesSelectionMapLayer({ selection }: LayerTypeSelectionMapLayerProps) {
  const { RHPdisplayBy } = useSnapshot(comparablesStore);
  const { unit: unitPreference } = useSnapshot(userStore.user.settings);

  const validSelectionTypes = [
    SelectionType.LANDTECH_COMPARABLE_TRANSACTION,
    SelectionType.HOMETRACK_COMPARABLE_TRANSACTION,
  ];

  const selectionGeometry = validSelectionTypes.includes(selection.type) ? selection?.feature : undefined;

  const fillColor = useMemo(() => {
    return (isZoopla(RHPdisplayBy) ? [126, 34, 206, 255] : [132, 204, 21, 255]) as Color;
  }, [RHPdisplayBy]);

  useMapLayer(() => {
    const getText = textAccessors[unitPreference || "metric"][RHPdisplayBy as CorePriceModes] || (() => "");

    return {
      layer: new ComparablesSelectionLayer({
        id: "comparables-detail-selection",
        selectionGeometry,
        fillColor,
        getText,
      }),
      zOrder: Infinity,
    };
  }, [selectionGeometry, fillColor, RHPdisplayBy, unitPreference]);

  return null;
}
